import * as React from 'react';

type ThemeMode = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';
type Dir = 'ltr' | 'rtl';

interface ThemeContextValue {
  theme: ThemeMode;
  resolved: Resolved;
  setTheme: (t: ThemeMode) => void;
  dir: Dir;
  setDir: (d: Dir) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const THEME_KEY = 'pakgames.admin.theme';
const DIR_KEY = 'pakgames.admin.dir';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<ThemeMode>(
    () => (localStorage.getItem(THEME_KEY) as ThemeMode) || 'system',
  );
  const [dir, setDirState] = React.useState<Dir>(() => (localStorage.getItem(DIR_KEY) as Dir) || 'ltr');
  const [resolved, setResolved] = React.useState<Resolved>(() =>
    theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme,
  );

  React.useEffect(() => {
    const compute = (): Resolved => (theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme);
    const apply = () => {
      const r = compute();
      setResolved(r);
      document.documentElement.setAttribute('data-theme', r);
    };
    apply();
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
  }, [theme]);

  React.useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.lang = dir === 'rtl' ? 'ur' : 'en';
  }, [dir]);

  const setTheme = React.useCallback((t: ThemeMode) => {
    localStorage.setItem(THEME_KEY, t);
    setThemeState(t);
  }, []);

  const setDir = React.useCallback((d: Dir) => {
    localStorage.setItem(DIR_KEY, d);
    setDirState(d);
  }, []);

  const value = React.useMemo(
    () => ({ theme, resolved, setTheme, dir, setDir }),
    [theme, resolved, setTheme, dir, setDir],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
